import type * as React from "react";
import { type ColumnDef, type Row } from "@tanstack/react-table";
import {
    CheckCircle,
    ChevronDown,
    ChevronRight,
    Copy,
    Pencil,
    Trash2,
    Undo2,
    XCircle,
} from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { STRING_MULTI_SELECT_BLANK } from "@/utils/tableMultiSelectFilter";
import type { TLineItemFacets } from "../ViewGLLines/lineItemQuery";
import type { TReviewLineItem } from "./types";

// Pinned "(Blanks)" filter option: selecting it keeps only rows whose value is
// empty/null. The sentinel value is understood server-side (see the backend
// `MULTISELECT_BLANK`); the DataTable filter popovers pin it to the top of the list.
const BLANK_FILTER_OPTION = { value: STRING_MULTI_SELECT_BLANK, label: "(Blanks)" };

// "Is Valid" maps to validation_status server-side; the option *values* are the
// backend status strings, with friendly labels. Used as a fallback when the
// facets endpoint does not return validation_statuses (empty or null).
const FALLBACK_VALIDATION_STATUS_OPTIONS = [
    { value: "valid", label: "Valid" },
    { value: "invalid", label: "Invalid" },
];

const VALIDATION_STATUS_LABEL: Record<string, string> = {
    valid: "Valid",
    invalid: "Invalid",
    warning: "Warning",
    pending: "Pending",
};

const prettifyStatus = (v: string): string =>
    VALIDATION_STATUS_LABEL[v] ?? v.charAt(0).toUpperCase() + v.slice(1);

const toOptions = (values: string[]): { value: string; label: string }[] =>
    values.map((v) => ({ value: v, label: prettifyStatus(v) }));

/** Per-row handlers for the Action column. */
export type TLineItemActionHandlers = {
    onEdit: (lineItem: TReviewLineItem) => void;
    /** Open the "number of splits" modal for a line that has not been split yet. */
    onSplit: (lineItem: TReviewLineItem) => void;
    /** Reverse a split — remove every split row, leaving the original as it was. */
    onReverseSplit: (lineItem: TReviewLineItem) => void;
    /** Append exactly one more split row to this row's original. */
    onAddSplitRow: (lineItem: TReviewLineItem) => void;
    onDeleteSplitRow: (lineItem: TReviewLineItem) => void;
};

/** What the current user/file state allows in the Action column. */
export type TLineItemActionGates = {
    /** Editing is blocked (file locked, or no line-edit permission). */
    editDisabled: boolean;
    /** The user holds tbgl_line_split and the file type supports splitting. */
    canSplit: boolean;
    /**
     * Starting a *new* split is blocked — the file is locked, or another split is
     * still incomplete. Reversal and the in-panel row actions stay enabled either
     * way, otherwise an incomplete split could never be fixed or undone.
     */
    splitDisabled: boolean;
};

const iconButtonClass =
    "p-1 hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

/** A row may be split only if it is a top-level, non-derived line. */
const isSplittable = (item: TReviewLineItem): boolean =>
    !item.isSplitChild && !item.isGenerated && !item.isRebalanceRow;

/**
 * The Action cell, which differs by the row's role in a split:
 *
 * - split row (`depth > 0`): edit, delete this row, add one more split row
 * - split original: expand/collapse, edit, reverse the split
 * - ordinary row: edit, split
 */
function renderActionCell(
    row: Row<TReviewLineItem>,
    handlers: TLineItemActionHandlers,
    gates: TLineItemActionGates,
): React.ReactNode {
    const item = row.original;
    const { editDisabled, canSplit, splitDisabled } = gates;
    const isSplitRow = row.depth > 0 || item.isSplitChild;
    const hasSplitRows = item.splitChildren.length > 0;
    const editTitle = editDisabled ? "Unavailable while the file is locked" : "Edit line item";

    const editButton = (
        <button
            type="button"
            data-testid={isSplitRow ? "split-row-edit-button" : "edit-line-item-button"}
            onClick={(e) => {
                e.stopPropagation();
                if (!editDisabled) handlers.onEdit(item);
            }}
            disabled={editDisabled}
            className={iconButtonClass}
            title={editTitle}
        >
            <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
        </button>
    );

    if (isSplitRow) {
        return (
            <div className="flex items-center gap-0.5">
                {editButton}
                <button
                    type="button"
                    data-testid="split-row-delete-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (canSplit) handlers.onDeleteSplitRow(item);
                    }}
                    disabled={!canSplit}
                    className={iconButtonClass}
                    title="Delete this split row"
                >
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                </button>
                <button
                    type="button"
                    data-testid="split-row-add-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (canSplit) handlers.onAddSplitRow(item);
                    }}
                    disabled={!canSplit}
                    className={iconButtonClass}
                    title="Add another split row"
                >
                    <Copy className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
            </div>
        );
    }

    // Derived rows (GL-generated, rebalance) are owned by their generator and are
    // never splittable, so they get the edit button alone.
    const showSplitControls = canSplit && isSplittable(item);

    return (
        <div className="flex items-center gap-0.5">
            {hasSplitRows ? (
                <button
                    type="button"
                    data-testid="split-expand-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                    }}
                    className={iconButtonClass}
                    title={row.getIsExpanded() ? "Hide split rows" : "Show split rows"}
                    aria-expanded={row.getIsExpanded()}
                >
                    {row.getIsExpanded() ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                </button>
            ) : (
                // Keep the column width stable so the edit buttons stay in one line.
                <span className="inline-block h-4 w-6" aria-hidden="true" />
            )}
            {editButton}
            {showSplitControls &&
                (hasSplitRows ? (
                    <button
                        type="button"
                        data-testid="split-reverse-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlers.onReverseSplit(item);
                        }}
                        className={iconButtonClass}
                        title="Reverse the split — removes the split rows and restores this row"
                    >
                        <Undo2 className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid="split-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!splitDisabled) handlers.onSplit(item);
                        }}
                        disabled={splitDisabled}
                        className={iconButtonClass}
                        title={
                            splitDisabled
                                ? "Unavailable while the file is locked or another split is incomplete"
                                : "Split this line item"
                        }
                    >
                        <Copy className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                ))}
        </div>
    );
}

// Line items table columns factory (server mode: filter types from the
// cardinality policy; multiSelect options from the facets endpoint).
export const createLineItemColumns = (
    handlers: TLineItemActionHandlers,
    gates: TLineItemActionGates,
    facets: TLineItemFacets | null,
): ColumnDef<TReviewLineItem>[] => {
    const f = facets;
    const text = { filterType: "text" as const };
    const numberRange = { filterType: "numberRange" as const };
    const date = { filterType: "date" as const };
    const multi = (values: string[]) => ({
        filterType: "multiSelect" as const,
        filterOptions: toOptions(values),
    });
    // Same as `multi`, plus a pinned "(Blanks)" option (mapped/output columns where
    // reviewers need to isolate unmapped rows, e.g. Propcode).
    const multiWithBlank = (values: string[]) => ({
        filterType: "multiSelect" as const,
        filterOptions: [BLANK_FILTER_OPTION, ...toOptions(values)],
    });
    // Same as `accountCodeRange`, plus a pinned "(Blanks)" option in the value list.
    const accountCodeRangeWithBlank = (values: string[]) => ({
        filterType: "numberRange" as const,
        filterOptions: [BLANK_FILTER_OPTION, ...values.map((v) => ({ value: v, label: v }))],
    });
    // Source validation-status options from the facets endpoint; fall back to
    // the static list when the server returns no values (e.g. on first load).
    const validationStatusOptions =
        f?.validation_statuses && f.validation_statuses.length > 0
            ? toOptions(f.validation_statuses)
            : FALLBACK_VALIDATION_STATUS_OPTIONS;

    return [
        {
            id: "action",
            header: "Action",
            size: 96,
            minSize: 96,
            cell: ({ row }) => renderActionCell(row, handlers, gates),
            enableSorting: false,
            enableColumnFilter: false,
            meta: { hidePerColumnTextSearch: true },
        },
        {
            accessorKey: "isValid",
            header: "Is Valid",
            size: 30,
            minSize: 30,
            cell: ({ row }) =>
                row.original.isValid ? (
                    <CheckCircle className="h-4 w-4 text-green-600" data-testid="line-item-valid" />
                ) : (
                    <Tooltip.Provider delayDuration={200}>
                        <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                                <span data-testid="line-item-invalid">
                                    <XCircle className="h-4 w-4 text-red-600" />
                                </span>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    side="right"
                                    sideOffset={5}
                                    data-testid="line-item-validation-errors"
                                    className="z-50 max-w-xs rounded bg-gray-900 px-3 py-2 text-xs text-white shadow-lg"
                                >
                                    {row.original.validationErrors.join(", ")}
                                    <Tooltip.Arrow className="fill-gray-900" />
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    </Tooltip.Provider>
                ),
            meta: {
                filterType: "multiSelect",
                filterOptions: validationStatusOptions,
                filterPlaceholder: "All",
            },
        },
        {
            accessorKey: "rowNumber",
            header: "Row Number",
            size: 30,
            minSize: 30,
            cell: ({ row }) => <span className="font-mono text-xs">{row.original.rowNumber}</span>,
            meta: numberRange,
        },
        {
            accessorKey: "trannum",
            header: "Trannum",
            size: 30,
            minSize: 30,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.trannum || "—"}</span>
            ),
            meta: numberRange,
        },
        {
            accessorKey: "propcode",
            header: "Mapped Entity",
            size: 90,
            minSize: 80,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.propcode || "—"}</span>
            ),
            meta: multiWithBlank(f?.mapped_property_codes ?? []),
        },
        {
            accessorKey: "localPropcode",
            header: "Entity",
            size: 90,
            minSize: 80,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.localPropcode || "—"}</span>
            ),
            meta: multiWithBlank(f?.property_codes ?? []),
        },
        {
            accessorKey: "asset",
            header: "Asset",
            size: 90,
            minSize: 80,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.asset || "—"}</span>
            ),
            meta: multiWithBlank(f?.asset_codes ?? []),
        },
        {
            accessorKey: "mappedAsset",
            header: "Mapped Asset",
            size: 90,
            minSize: 80,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.mappedAsset || "—"}</span>
            ),
            meta: multiWithBlank(f?.mapped_asset_codes ?? []),
        },
        {
            accessorKey: "investment",
            header: "Investment",
            size: 90,
            minSize: 80,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.investment || "—"}</span>
            ),
            meta: multiWithBlank(f?.investment_codes ?? []),
        },
        {
            accessorKey: "mappedInvestment",
            header: "Mapped Investment",
            size: 90,
            minSize: 80,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.mappedInvestment || "—"}</span>
            ),
            meta: multiWithBlank(f?.mapped_investment_codes ?? []),
        },
        {
            accessorKey: "sourceSystem",
            header: "Source System",
            size: 90,
            minSize: 80,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.sourceSystem || "—"}</span>
            ),
            meta: multiWithBlank(f?.source_systems ?? []),
        },
        {
            accessorKey: "sourceAccountName",
            header: "Source Acc Name",
            size: 120,
            minSize: 100,
            cell: ({ row }) => (
                <div
                    className="text-xs truncate"
                    title={row.original.sourceAccountName || undefined}
                >
                    {row.original.sourceAccountName || "—"}
                </div>
            ),
            meta: { filterType: "text" as const, filterBlankOption: true },
        },
        {
            accessorKey: "sourceAccountNumber",
            header: "Source Acc No",
            size: 90,
            minSize: 80,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.sourceAccountNumber}</span>
            ),
            meta: accountCodeRangeWithBlank(f?.account_codes ?? []),
        },
        {
            accessorKey: "breAccountName",
            header: "Acc Name",
            size: 120,
            minSize: 100,
            cell: ({ row }) => (
                <div className="text-xs truncate" title={row.original.breAccountName || undefined}>
                    {row.original.breAccountName || "—"}
                </div>
            ),
            meta: { filterType: "text" as const, filterBlankOption: true },
        },
        {
            accessorKey: "breAccountNumber",
            header: "Acc No",
            size: 80,
            minSize: 70,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.breAccountNumber || "—"}</span>
            ),
            meta: accountCodeRangeWithBlank(f?.mapped_account_codes ?? []),
        },
        {
            accessorKey: "amount",
            header: "Amount",
            size: 100,
            minSize: 90,
            cell: ({ row }) => (
                <span className="font-mono text-xs text-right block">
                    {parseFloat(row.original.amount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}
                </span>
            ),
            meta: numberRange,
        },
        {
            accessorKey: "transactionDate",
            header: "Date",
            size: 90,
            minSize: 80,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.transactionDate || "—"}</span>
            ),
            meta: date,
        },
        {
            accessorKey: "postMonth",
            header: "Post Month",
            size: 80,
            minSize: 70,
            cell: ({ row }) => {
                const val = row.original.postMonth;
                const display = val ? val.slice(0, 7) : "—";
                return <span className="font-mono text-xs">{display}</span>;
            },
            meta: date,
        },
        {
            accessorKey: "bookNumber",
            header: "Book Number",
            size: 60,
            minSize: 50,
            cell: ({ row }) => <span className="font-mono text-xs">{row.original.bookNumber}</span>,
            meta: multi(f?.book_numbers ?? []),
        },
        {
            accessorKey: "description",
            header: "Description",
            size: 150,
            minSize: 120,
            cell: ({ row }) => (
                <div className="text-xs truncate" title={row.original.description || undefined}>
                    {row.original.description || "—"}
                </div>
            ),
            meta: text,
        },
        {
            accessorKey: "ref",
            header: "Ref",
            size: 70,
            minSize: 60,
            cell: ({ row }) => <span className="font-mono text-xs">{row.original.ref || "—"}</span>,
            meta: text,
        },
        {
            accessorKey: "exchangeRate",
            enableSorting: false,
            header: "Exchange Rate",
            size: 80,
            minSize: 70,
            enableColumnFilter: false,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.exchangeRate || "—"}</span>
            ),
        },
        {
            accessorKey: "exchangeRateDate",
            enableSorting: false,
            header: "Exchange Rate Date",
            size: 90,
            minSize: 80,
            enableColumnFilter: false,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.exchangeRateDate || "—"}</span>
            ),
        },
        {
            accessorKey: "displayType",
            header: "Display Type",
            size: 80,
            minSize: 70,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.displayType || "—"}</span>
            ),
            meta: multi(f?.display_types ?? []),
        },
        {
            accessorKey: "localCurrency",
            header: "Local Currency",
            size: 60,
            minSize: 50,
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.localCurrency || "—"}</span>
            ),
            meta: multi(f?.currencies ?? []),
        },
        {
            accessorKey: "localAmount",
            enableSorting: false,
            header: "Local Amount",
            size: 100,
            minSize: 90,
            enableColumnFilter: false,
            cell: ({ row }) => (
                <span className="font-mono text-xs text-right block">
                    {row.original.localAmount
                        ? parseFloat(row.original.localAmount).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                          })
                        : "—"}
                </span>
            ),
        },
    ];
};
